import type { ReactNode } from 'react';
import { SolidStatusPill } from '../../components/SolidStatusPill';
import type { DataTableColumn } from '../types';
import { buildColumn, EMPTY_VALUE, type ColumnPresetOptions } from './base';

/**
 * Maps a raw status value to pill classes. Either a lookup object or a
 * function; both must return a **background and a text colour**, because the
 * tone replaces the pill's hashed background outright (see
 * `SolidStatusPill.toneClassName`).
 */
export type StatusTone = Readonly<Record<string, string>> | ((value: string) => string | undefined);

function resolveTone(tone: StatusTone | undefined, value: string): string | undefined {
    if (!tone) {
        return undefined;
    }
    return typeof tone === 'function' ? tone(value) : tone[value];
}

export type StatusColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** The raw status value. Also the colour-hash input when no tone matches. */
    value: (row: T) => string | null | undefined;
    /** Humanized display text. Defaults to the raw value. */
    getLabel?: (row: T) => string | undefined;
    /**
     * Optional colour mapping. Unmapped values keep `SolidStatusPill`'s hashed
     * colour, so a brand-new status still renders consistently everywhere
     * without a code change.
     */
    tone?: StatusTone;
    fallback?: ReactNode;
    /** Extra classes on the pill (sizing, max-width, …). */
    pillClassName?: string;
};

/**
 * A status pill, rendered with the shared `SolidStatusPill` - deliberately not
 * a second badge component. The pill carries its own tooltip and truncation,
 * so this column defaults `truncate` **off**: clipping the cell as well would
 * cut the pill's rounded end off.
 */
export function statusColumn<T, K extends string = string>(
    options: StatusColumnOptions<T, K>
): DataTableColumn<T, K> {
    const { value, getLabel, tone, fallback = EMPTY_VALUE, pillClassName } = options;

    return buildColumn(
        options,
        (row) => {
            const raw = value(row);
            if (!raw) {
                return fallback;
            }
            return (
                <SolidStatusPill
                    value={raw}
                    label={getLabel?.(row)}
                    toneClassName={resolveTone(tone, raw)}
                    className={pillClassName}
                />
            );
        },
        { truncate: false }
    );
}
